import { UserProfile, Role, GelanggangDetail, InvoiceItem } from '../types';

/**
 * Helper utility to format display names based on page context.
 * 
 * Rules for Public Pages:
 * 1. Uses the name before 'bin', 'binti', 'bte', or 'bt' (case-insensitive). 'bin' and subsequent words are omitted.
 * 2. If name has no 'bin' / 'binti', returns the first 2 words of the name to keep it concise.
 * 3. Handles edge cases cleanly (empty names, single word names, etc.).
 * 
 * Rules for Dashboard / Portal Pages:
 * - Keeps full original name.
 */

export function formatPublicName(name?: string | null): string {
  if (!name) return '';
  const trimmed = name.trim();
  if (!trimmed) return '';

  // Check if name contains 'bin', 'binti', 'bte', or 'bt' (case-insensitive) as a word separator
  const binMatch = trimmed.match(/^(.+?)\s+(?:bin|binti|bte|bt)(?:\s+|$)/i);
  if (binMatch && binMatch[1]) {
    return binMatch[1].trim();
  }

  // If no bin/binti, return first 2 words
  const words = trimmed.split(/\s+/);
  if (words.length <= 2) {
    return trimmed;
  }
  return words.slice(0, 2).join(' ');
}

/**
 * Returns seller display name showing only the given name before 'bin' / 'binti'.
 */
export function formatSellerName(name?: string | null): string {
  if (!name) return 'Persatuan Seni Silat ASMM';
  const trimmed = name.trim();
  if (!trimmed) return 'Persatuan Seni Silat ASMM';

  const binMatch = trimmed.match(/^(.+?)\s+(?:bin|binti|bte|bt)\b/i);
  if (binMatch && binMatch[1]) {
    return binMatch[1].trim();
  }

  return trimmed;
}

/**
 * Constructs a valid Google Maps navigation link from Gelanggang details.
 * Accepts full URLs (http/https), GPS coordinates (lat,lng), or physical address text.
 */
export function getGoogleMapsUrl(g?: { gpsLocation?: string; location?: string; address?: string; name?: string } | null): string {
  if (!g) return 'https://maps.google.com';

  if (g.gpsLocation && g.gpsLocation.trim()) {
    const raw = g.gpsLocation.trim();
    if (raw.startsWith('http://') || raw.startsWith('https://')) {
      return raw;
    }
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(raw)}`;
  }

  const searchQuery = getGelanggangDisplayAddress(g) || g?.name || 'Gelanggang ASMM';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(searchQuery)}`;
}

/**
 * Resolves the physical address or GPS location text to display on Gelanggang cards.
 * Prevents session time (e.g. "Jumaat & Sabtu (8.00 AM)") from showing as the address.
 */
export function getGelanggangDisplayAddress(g?: { address?: string; gpsLocation?: string; location?: string; sessionTime?: string; name?: string } | null): string {
  if (!g) return '';

  const isInvalid = (val?: string) => {
    if (!val || !val.trim()) return true;
    const v = val.trim().toLowerCase();
    if (g.sessionTime && v === g.sessionTime.trim().toLowerCase()) return true;
    if (v.includes('jumaat') || v.includes('sabtu') || v.includes('ahad') || v.includes('rabu') || v.includes('isnin') || v.includes('selasa') || v.includes('khamis')) {
      if (v.includes('8.00') || v.includes('8.30') || v.includes('9.00') || v.includes('am') || v.includes('pm')) {
        return true; // Contains session time string instead of address
      }
    }
    return false;
  };

  if (!isInvalid(g.address)) return g.address!.trim();
  if (!isInvalid(g.location)) return g.location!.trim();
  if (!isInvalid(g.gpsLocation) && !g.gpsLocation!.startsWith('http')) return g.gpsLocation!.trim();

  return g.name || '';
}

/**
 * Calculates member age rounded by calendar year (genapkan umur mengikut tahun kelahiran).
 * Contoh: Lahir November 2018, pada tahun 2026 umur digenapkan ke 8 tahun (2026 - 2018),
 * walaupun tarikh/bulan kelahiran belum tiba pada tahun semasa.
 * Menyokong input IC No (YYMMDD), tarikh lahir (YYYY-MM-DD), atau tahun lahir (number).
 */
export function calculateAge(input?: string | number | Date | null, referenceYear?: number): number {
  if (!input) return 0;
  const currentYear = referenceYear || new Date().getFullYear();

  // If input is already a 4-digit birth year number (e.g. 2018)
  if (typeof input === 'number') {
    if (input >= 1900 && input <= currentYear) {
      return Math.max(0, currentYear - input);
    }
  }

  // If input is a Date object
  if (input instanceof Date) {
    if (!isNaN(input.getTime())) {
      return Math.max(0, currentYear - input.getFullYear());
    }
  }

  const str = String(input).trim();

  // If input is a standard date string like YYYY-MM-DD
  const dateMatch = str.match(/^(\d{4})[-/.]\d{1,2}[-/.]\d{1,2}/);
  if (dateMatch) {
    const y = parseInt(dateMatch[1], 10);
    return Math.max(0, currentYear - y);
  }

  // Otherwise assume Malaysian IC number (YYMMDD...)
  const cleanIc = str.replace(/[^0-9]/g, '');
  if (cleanIc.length < 6) return 0;

  const yy = parseInt(cleanIc.substring(0, 2), 10);
  if (isNaN(yy)) return 0;

  const currentYearShort = currentYear % 100;
  const birthYear = yy <= currentYearShort ? 2000 + yy : 1900 + yy;

  return Math.max(0, currentYear - birthYear);
}

/**
 * Extracts parsed details from Malaysian IC number.
 * Age is rounded by calendar year (genapkan umur mengikut tahun kelahiran).
 */
export function getIcDetails(icNo?: string, referenceYear?: number): {
  age: number;
  gender: 'Lelaki' | 'Perempuan' | null;
  category: 'Kanak-kanak' | 'Remaja' | 'Dewasa' | 'Warga Emas';
  birthYear: number;
} | null {
  if (!icNo) return null;
  const clean = icNo.replace(/\D/g, '');
  if (clean.length < 6) return null;

  const yy = parseInt(clean.substring(0, 2), 10);
  if (isNaN(yy)) return null;

  const currentYear = referenceYear || new Date().getFullYear();
  const currentYearShort = currentYear % 100;
  const birthYear = yy <= currentYearShort ? 2000 + yy : 1900 + yy;
  const age = Math.max(0, currentYear - birthYear);

  let gender: 'Lelaki' | 'Perempuan' | null = null;
  if (clean.length === 12) {
    const lastDigit = parseInt(clean.charAt(11), 10);
    if (!isNaN(lastDigit)) {
      gender = lastDigit % 2 === 1 ? 'Lelaki' : 'Perempuan';
    }
  }

  let category: 'Kanak-kanak' | 'Remaja' | 'Dewasa' | 'Warga Emas' = 'Dewasa';
  if (age < 13) category = 'Kanak-kanak';
  else if (age < 18) category = 'Remaja';
  else if (age >= 55) category = 'Warga Emas';

  return { age, gender, category, birthYear };
}

/**
 * Checks if a user is above or equal to 18 years old based on their Malaysian IC number.
 * Menggunakan pengiraan umur genap mengikut tahun kelahiran (currentYear - birthYear >= 18).
 */
export function isUserAbove18(icNo?: string, referenceYear?: number): boolean {
  if (!icNo) return false;
  return calculateAge(icNo, referenceYear) >= 18;
}

/**
 * Checks if a user has Superadmin privileges (full system access).
 * Specifically true for meisterhafiz@gmail.com / asmm-011.
 * Operates completely in the background with NO UI badges or labels.
 */
export const ROLE_HIERARCHY: Record<string, number> = {
  'Superadmin': 100,
  'Super Admin': 100,
  'Pengerusi': 90,
  'Timbalan Pengerusi': 85,
  'Guru Utama': 80,
  'Setiausaha': 75,
  'Bendahari': 70,
  'Penolong Bendahari': 68,
  'Pen. Bendahari': 68,
  'Pen. Setiausaha': 65,
  'Penolong Setiausaha': 65,
  'Juru Audit Dalaman': 60,
  'Juruaudit Dalaman': 60,
  'Juru Audit': 60,
  'Juruaudit': 60,
  'Pemeriksa Kira-Kira': 60,
  'Ketua Gelanggang': 55,
  'Bendahari Gelanggang': 50,
  'Ketua Biro Latihan & Dakwah': 48,
  'Ketua Biro Sosial & Kebajikan': 48,
  'Ketua Biro Keusahawanan & Sukan': 48,
  'Ketua Biro Media & Penerangan': 48,
  'Jurulatih Kanan': 45,
  'Jurulatih': 40,
  'Penolong Jurulatih': 35,
  'Pembantu Jurulatih': 30,
  'AJK': 25,
  'Ahli Jawatankuasa': 25,
  'Waris': 15,
  'Ahli': 10,
};

export function getRoleScore(role?: string | null): number {
  if (!role) return 0;
  const direct = ROLE_HIERARCHY[role];
  if (direct !== undefined) return direct;
  const rLower = role.toLowerCase().trim();
  if (rLower.includes('superadmin') || rLower.includes('super admin')) return 100;
  if (rLower.includes('pengerusi')) return 90;
  if (rLower.includes('guru utama')) return 80;
  if (rLower.includes('setiausaha')) return 75;
  if (rLower.includes('penolong bendahari') || rLower.includes('pen. bendahari')) return 68;
  if (rLower.includes('bendahari') && !rLower.includes('gelanggang')) return 70;
  if (rLower.includes('penolong setiausaha') || rLower.includes('pen. setiausaha')) return 65;
  if (rLower.includes('audit') || rLower.includes('pemeriksa')) return 60;
  if (rLower.includes('ketua gelanggang')) return 55;
  if (rLower.includes('bendahari gelanggang')) return 50;
  if (rLower.includes('ketua biro') || rLower.includes('biro')) return 48;
  if (rLower.includes('jurulatih kanan')) return 45;
  if (rLower.includes('jurulatih')) return 40;
  if (rLower.includes('ajk')) return 25;
  if (rLower.includes('waris')) return 15;
  if (rLower.includes('ahli')) return 10;
  return 5;
}

/**
 * Checks if a user has Superadmin privileges (full system access).
 * Superadmin surpasses all restrictions and possesses master executive access.
 * Specifically true for meisterhafiz@gmail.com / asmm-011.
 * Operates completely in the background with NO UI badges or labels.
 */
export function isSuperAdmin(user?: UserProfile | null): boolean {
  if (!user) return false;
  const email = (user.email || '').toLowerCase().trim();
  const id = (user.id || '').toLowerCase().trim();
  const userRoles = (user.roles || []).map(r => (r || '').toLowerCase().trim());
  return (
    email === 'meisterhafiz@gmail.com' ||
    email === 'hafiz@asmm.org.my' ||
    id === 'asmm-011' ||
    id === 'usr-ahli-05' ||
    user.role === 'Superadmin' ||
    user.role === 'Super Admin' ||
    userRoles.includes('superadmin') ||
    userRoles.includes('super admin')
  );
}

/**
 * Checks if a given role is an executive / management committee role.
 */
export function isPengurusanRole(role?: string | null): boolean {
  if (!role) return false;
  const r = role.toLowerCase().trim();
  return (
    r === 'superadmin' ||
    r === 'super admin' ||
    r === 'pengerusi' ||
    r === 'timbalan pengerusi' ||
    r === 'guru utama' ||
    r === 'setiausaha' ||
    r === 'pen. setiausaha' ||
    r === 'penolong setiausaha' ||
    r === 'bendahari' ||
    r === 'pen. bendahari' ||
    r === 'penolong bendahari' ||
    r === 'ketua gelanggang' ||
    r === 'bendahari gelanggang' ||
    r === 'ajk' ||
    r === 'ahli jawatankuasa' ||
    r === 'pemeriksa kira-kira' ||
    r === 'juru audit dalaman' ||
    r === 'juruaudit dalaman' ||
    r === 'juru audit' ||
    r === 'juruaudit' ||
    r.includes('audit') ||
    r.startsWith('ketua biro') ||
    r.includes('biro')
  );
}

/**
 * Checks if a user has management-level privileges based on any of their roles.
 * Superadmin automatically surpasses all.
 */
export function isUserManagement(user?: UserProfile | null): boolean {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;
  const roles = getUserRoles(user);
  return roles.some(r => isPengurusanRole(r));
}

/**
 * Retrieves all assigned roles for a user as an array, prioritizing higher roles.
 * Fallbacks cleanly to [user.role] or ['Ahli'] if user has no multiple roles specified.
 */
export function getUserRoles(user?: UserProfile | null): Role[] {
  if (!user) return [];
  let roles: Role[] = [];
  if (user.roles && Array.isArray(user.roles) && user.roles.length > 0) {
    roles = Array.from(new Set(user.roles));
  } else if (user.role) {
    roles = [user.role];
  } else {
    roles = ['Ahli'];
  }

  // If user is superadmin by email/id, ensure Superadmin is in roles
  if (isSuperAdmin(user) && !roles.includes('Superadmin')) {
    roles = ['Superadmin', ...roles];
  }

  // Sort descending by role hierarchy (highest role first)
  return roles.sort((a, b) => getRoleScore(b) - getRoleScore(a));
}

/**
 * Returns the highest priority role among all roles assigned to the user.
 * Superadmin always surpasses all other roles.
 */
export function getHighestRole(user?: UserProfile | null): Role {
  if (!user) return 'Ahli';
  if (isSuperAdmin(user)) return 'Superadmin';
  const roles = getUserRoles(user);
  return roles[0] || user.role || 'Ahli';
}

/**
 * Checks if a user has a specific role (case-insensitive) either as their primary role or in their multiple roles.
 * Superadmin automatically surpasses all role requirements.
 */
export function hasUserRole(user?: UserProfile | null, targetRole?: string | null): boolean {
  if (!user || !targetRole) return false;
  if (isSuperAdmin(user)) return true;
  const roles = getUserRoles(user);
  const target = targetRole.toLowerCase().trim();
  return roles.some(r => (r || '').toLowerCase().trim() === target);
}

/**
 * Checks if a user is solely an ordinary / general member without administrative or coach appointments.
 * General member roles: 'Ahli', 'Ahli Biasa', 'Ahli Seumur Hidup', 'Waris'.
 */
export function isGeneralMemberOnly(user?: UserProfile | null): boolean {
  if (!user) return true;
  if (isSuperAdmin(user)) return false;
  const roles = getUserRoles(user).map(r => (r || '').toLowerCase().trim());
  if (roles.length === 0) return true;
  const generalRoles = ['ahli', 'ahli biasa', 'ahli seumur hidup', 'waris'];
  return roles.every(r => generalRoles.includes(r));
}

/**
 * Checks if a user is a certified trainer / coach (Jurulatih, Pembantu Jurulatih, Guru Utama)
 * based on role or advanced bengkung rank (Merah / Kuning / Guru Utama).
 */
export function isJurulatih(user?: UserProfile | null): boolean {
  if (!user) return false;
  // If user holds solely general membership (Ahli, Ahli Seumur Hidup, Waris), they are not an appointed trainer
  if (isGeneralMemberOnly(user)) return false;

  const roles = getUserRoles(user);
  const hasCoachRole = roles.some(r => {
    const roleLower = (r || '').toLowerCase().trim();
    return (
      roleLower === 'jurulatih' ||
      roleLower === 'jurulatih kanan' ||
      roleLower === 'jurulatih muda' ||
      roleLower === 'guru utama' ||
      roleLower.includes('jurulatih') ||
      roleLower.includes('guru utama')
    );
  });
  if (hasCoachRole) return true;

  const bengkung = (user.bengkung || '').toLowerCase().trim();
  return (
    bengkung.includes('kuning') ||
    bengkung.includes('guru utama')
  );
}

/**
 * Checks if a user is authorized at management level:
 * Superadmin, Jurulatih, Ketua Gelanggang, or AJK / Management Committee list.
 */
export function isManagementOrTrainer(user?: UserProfile | null): boolean {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;
  if (isGeneralMemberOnly(user)) return false;
  const roles = getUserRoles(user);
  if (roles.some(r => r === 'Ketua Gelanggang' || isPengurusanRole(r))) return true;
  return isJurulatih(user);
}

/**
 * Accurately determines if a member is a child/dependent of a given parent/waris.
 * Supports case-insensitive ID matching, IC matching, kin phone matching,
 * and canonical bindings (e.g. Aisya ASMM-014 and Ahmad Malik ASMM-016 for Hafiz Halim ASMM-011).
 */
export function isChildOfParent(
  child?: UserProfile | null,
  parent?: UserProfile | null
): boolean {
  if (!child || !parent) return false;

  // 1. Direct parentId match (case-insensitive)
  if (child.parentId && parent.id && child.parentId.trim() && child.parentId.trim().toLowerCase() === parent.id.trim().toLowerCase()) {
    return true;
  }

  // 2. Match by IC number if parentId was stored as IC
  if (child.parentId && parent.icNo && child.parentId.trim() && sanitizeIc(child.parentId) === sanitizeIc(parent.icNo)) {
    return true;
  }

  // 3. Match by kinPhone for family members / dependents
  if (
    child.kinPhone && 
    parent.phone && 
    child.kinPhone.replace(/\D/g, '').length >= 8 && 
    parent.phone.replace(/\D/g, '').length >= 8 && 
    sanitizePhone(child.kinPhone) === sanitizePhone(parent.phone)
  ) {
    return true;
  }

  // 4. Explicit binding for Mohammad Hafiz Bin Halim (ASMM-011) and his children: Aisya (ASMM-014) & Ahmad Malik (ASMM-016)
  const isParentHafiz = (
    (parent.id && parent.id.toLowerCase() === 'asmm-011') ||
    (parent.icNo && parent.icNo.trim() === '030819025819') ||
    (parent.email && parent.email.toLowerCase() === 'meisterhafiz@gmail.com') ||
    (parent.name && parent.name.toLowerCase().includes('hafiz') && parent.name.toLowerCase().includes('halim'))
  );

  if (isParentHafiz) {
    const isTargetAisyaOrMalik = (
      (child.id && ['asmm-014', 'asmm-016'].includes(child.id.toLowerCase())) ||
      (child.icNo && ['161205021110', '181120025619'].includes(child.icNo.trim())) ||
      (child.name && (
        child.name.toLowerCase().includes('aisya') || 
        child.name.toLowerCase().includes('ahmad malik')
      ))
    );
    if (isTargetAisyaOrMalik) return true;
  }

  return false;
}

/**
 * Checks if two members belong to the same immediate family unit.
 */
export function isFamilyMember(
  memberA?: UserProfile | null,
  memberB?: UserProfile | null
): boolean {
  if (!memberA || !memberB) return false;
  if (memberA.id && memberB.id && memberA.id.toLowerCase() === memberB.id.toLowerCase()) return true;
  if (memberA.icNo && memberB.icNo && sanitizeIc(memberA.icNo) === sanitizeIc(memberB.icNo)) return true;
  if (isChildOfParent(memberA, memberB)) return true;
  if (isChildOfParent(memberB, memberA)) return true;
  if (memberA.parentId && memberB.parentId && memberA.parentId.trim() && memberA.parentId.trim().toLowerCase() === memberB.parentId.trim().toLowerCase()) return true;
  return false;
}

/**
 * Determines whether a viewer has permission to view another member's sensitive personal data
 * (IC number, phone number, email address, home address, etc.).
 * 
 * Rules:
 * - Superadmin, Management (Pengurusan), and Trainers (Jurulatih/Ketua Gelanggang) CAN view all.
 * - General members (Ahli biasa, Ahli Seumur Hidup, Waris) CANNOT view sensitive data of other members ("orang lain").
 * - A user CAN always view their own sensitive data.
 * - A Waris / Parent CAN only view their own registered child / dependent.
 */
export function canViewMemberSensitiveInfo(
  viewer?: UserProfile | null,
  target?: UserProfile | null
): boolean {
  if (!viewer) return false;

  // 1. Viewing own details is always permitted
  if (target) {
    const isSelf = (
      (viewer.id && target.id && viewer.id.trim().toLowerCase() === target.id.trim().toLowerCase()) ||
      (viewer.icNo && target.icNo && sanitizeIc(viewer.icNo) === sanitizeIc(target.icNo))
    );
    if (isSelf) return true;
  }

  // 2. Waris / Parent can view sensitive details of their own registered child / dependent
  if (target && isChildOfParent(target, viewer)) {
    return true;
  }

  // 3. General members (Ahli, Ahli Biasa, Ahli Seumur Hidup, Waris) CANNOT view sensitive data of other members ("orang lain")
  // Even if they possess martial bengkung ranks, general members do not hold administrative data clearance.
  if (isGeneralMemberOnly(viewer)) {
    return false;
  }

  // 4. Superadmin has administrative master access
  if (isSuperAdmin(viewer)) return true;

  // 5. Official Management committee & Appointed Trainers have clearance for association administration
  if (isManagementOrTrainer(viewer)) return true;

  return false;
}

/**
 * Checks whether a user satisfies role requirements, granting automatic bypass access if Superadmin.
 * Checks all roles assigned to the user.
 */
export function hasRolePermission(user?: UserProfile | null, allowedRoles?: string[]): boolean {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;
  if (!allowedRoles || allowedRoles.length === 0) return true;
  const userRoles = getUserRoles(user).map(r => (r || '').toLowerCase().trim());
  const normalizedAllowed = allowedRoles.map(a => (a || '').toLowerCase().trim());
  return userRoles.some(r => normalizedAllowed.includes(r));
}

/**
 * Checks if a user is authorized to add child details (Waris OR Ahli above 18, or Superadmin/Management).
 * Evaluates across all assigned roles of the user.
 */
export function canUserAddChild(user?: UserProfile | null): boolean {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;
  const roles = getUserRoles(user);
  const isManagement = roles.some(r => isPengurusanRole(r));
  if (isManagement) return true;
  if (roles.includes('Waris')) return true;
  if (roles.includes('Ahli')) {
    return isUserAbove18(user.icNo);
  }
  return false;
}

/**
 * Checks whether a Waris user is missing required registered/linked child.
 * Waris accounts are strictly forced to have at least 1 child before accessing the dashboard.
 * Management, trainers, and Superadmin are exempt from this gate.
 */
export function isWarisMissingChild(user?: UserProfile | null, members: UserProfile[] = []): boolean {
  if (!user) return false;
  if (isSuperAdmin(user)) return false;

  // Management / committee bypass across all assigned roles
  const userRoles = getUserRoles(user);
  if (userRoles.some(r => isPengurusanRole(r)) || isJurulatih(user)) {
    return false;
  }

  // Must be Waris (either primary role or within assigned roles)
  const isWaris = userRoles.includes('Waris') || user.role === 'Waris';
  if (!isWaris) return false;

  // If user has other active roles (e.g. Ahli, Pesilat, Jurulatih), they are not a pure waris-only account
  if (userRoles.some(r => r !== 'Waris')) {
    return false;
  }

  // Check if any member in the system is linked as child of this user
  const hasChild = members.some(m => isChildOfParent(m, user));
  return !hasChild;
}

/**
 * Formats a number as Malaysian Ringgit currency (e.g. RM 1,500.00 or RM 50).
 */
export function formatCurrencyMYR(amount: number): string {
  const num = Number(amount) || 0;
  return `RM ${num.toLocaleString('en-MY', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Validates email format.
 */
export function isValidEmail(email?: string | null): boolean {
  if (!email) return false;
  const trimmed = email.trim();
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(trimmed);
}

/**
 * Converts a person's name or text to Title Case (e.g. "NAJWA BINTI ABDUL AZIS" -> "Najwa Binti Abdul Azis").
 */
export function toTitleCase(name?: string | null): string {
  if (!name) return '';
  const trimmed = name.trim().replace(/\s+/g, ' ');
  if (!trimmed) return '';

  return trimmed
    .split(' ')
    .map(word => {
      if (!word) return '';
      const lower = word.toLowerCase();
      if (lower === 'a/l') return 'a/l';
      if (lower === 'a/p') return 'a/p';

      if (word.includes('-')) {
        return word
          .split('-')
          .map(part => part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : '')
          .join('-');
      }

      if (word.startsWith("'") || word.startsWith('"')) {
        return word.charAt(0) + (word.charAt(1) ? word.charAt(1).toUpperCase() + word.slice(2).toLowerCase() : '');
      }

      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Sanitizes Malaysian IC numbers by removing dashes, spaces, and all symbols (only digits).
 */
export function sanitizeIc(ic?: string | null): string {
  if (!ic) return '';
  return ic.replace(/\D/g, '').trim();
}

/**
 * Sanitizes phone numbers by removing spaces and symbols (only numbers), and standardizing Malaysian mobile prefixes (+60 / 60 -> 0).
 */
export function sanitizePhone(phone?: string | null): string {
  if (!phone) return '';
  let cleaned = phone.replace(/\D/g, '').trim();
  if (cleaned.startsWith('60') && cleaned.length >= 11) {
    cleaned = '0' + cleaned.slice(2);
  }
  return cleaned;
}

/**
 * Checks whether an IC number is already registered to another member in the system.
 */
export function findDuplicateIcMember(
  ic?: string | null,
  excludeMemberId?: string | null,
  members: UserProfile[] = []
): UserProfile | undefined {
  const clean = sanitizeIc(ic);
  if (!clean || clean.length < 6) return undefined;
  const excId = (excludeMemberId || '').trim().toLowerCase();
  return members.find(m => 
    (!excId || (m.id && m.id.trim().toLowerCase() !== excId)) &&
    m.icNo && 
    sanitizeIc(m.icNo) === clean
  );
}

/**
 * Checks whether a phone number is already registered to another member in the system.
 */
export function findDuplicatePhoneMember(
  phone?: string | null,
  excludeMemberId?: string | null,
  members: UserProfile[] = []
): UserProfile | undefined {
  const clean = sanitizePhone(phone);
  if (!clean || clean.length < 8) return undefined;
  const excId = (excludeMemberId || '').trim().toLowerCase();
  return members.find(m => 
    (!excId || (m.id && m.id.trim().toLowerCase() !== excId)) &&
    m.phone && 
    sanitizePhone(m.phone) === clean
  );
}

/**
 * Sanitizes and formats a member ID to be displayed in the front end as all caps.
 * E.g. 'asmm-001' -> 'ASMM-001', ' asmm-015 ' -> 'ASMM-015', 'usr-ahli-05' -> 'USR-AHLI-05'
 */
export function sanitizeMemberId(id?: string | null): string {
  if (!id) return '';
  return id.trim().toUpperCase();
}

export const formatMemberId = sanitizeMemberId;

/**
 * Checks whether a user has filled all required personal details.
 * Enforces mandatory: Name, IC number, Phone number, Residential address, and Gelanggang (for pesilat).
 */
export function isProfileComplete(user?: UserProfile | null): boolean {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;

  const cleanName = (user.name || '').trim();
  const cleanIc = sanitizeIc(user.icNo);
  const cleanPhone = sanitizePhone(user.phone);
  const cleanAddress = (user.address || '').trim();

  // Name check
  if (!cleanName || cleanName === 'Pesilat ASMM' || cleanName.length < 2) {
    return false;
  }

  // IC Number check (at least 6-12 numeric characters)
  if (!cleanIc || cleanIc.length < 6) {
    return false;
  }

  // Phone number check (at least 8 digits)
  if (!cleanPhone || cleanPhone.length < 8) {
    return false;
  }

  // Residential address check (at least 5 chars)
  if (!cleanAddress || cleanAddress.length < 5) {
    return false;
  }

  // If role is Ahli (Pesilat), gelanggang must be specified
  if (user.role === 'Ahli' || (!user.role && user.role !== 'Waris')) {
    if (!user.gelanggang || user.gelanggang === ('Tiada' as any) || user.gelanggang.trim() === '') {
      return false;
    }
  }

  return true;
}

/**
 * Authorizes access to edit Gelanggang session schedules and pricing:
 * - Superadmin can edit all gelanggang.
 * - Only the respective Ketua Gelanggang can edit their own gelanggang.
 * - All other roles / members cannot edit.
 */
export function canEditGelanggangSessionAndPrice(
  user?: UserProfile | null,
  g?: GelanggangDetail | null
): boolean {
  if (!user || !g) return false;
  if (isSuperAdmin(user)) return true;

  if (user.role === 'Ketua Gelanggang') {
    // 1. Check normalized gelanggang name match
    if (user.gelanggang && g.name) {
      const uGel = user.gelanggang.toLowerCase().replace(/gelanggang\s+/gi, '').trim();
      const gGel = g.name.toLowerCase().replace(/gelanggang\s+/gi, '').trim();
      if (uGel && gGel && (uGel === gGel || uGel.includes(gGel) || gGel.includes(uGel))) {
        return true;
      }
    }

    // 2. Check Ketua Gelanggang name match with leaderName
    if (user.name && g.leaderName) {
      const uName = user.name.toLowerCase().trim();
      const lName = g.leaderName.toLowerCase().trim();
      if (uName && lName && (uName === lName || uName.includes(lName) || lName.includes(uName))) {
        return true;
      }
    }

    // 3. Check phone number match with leaderPhone
    if (user.phone && g.leaderPhone) {
      const p1 = user.phone.replace(/[^0-9]/g, '');
      const p2 = g.leaderPhone.replace(/[^0-9]/g, '');
      if (p1 && p2 && (p1 === p2 || p1.endsWith(p2) || p2.endsWith(p1))) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Determines whether an invoice is strictly related to a given user (Ahli or Waris).
 * 
 * Rules:
 * - Vendor / 3rd party invoices are NEVER related to regular members or waris.
 * - Ahli: Invoices where memberId, memberIcNo, or full name directly matches the user,
 *   or if user is a registered adult parent of the student.
 * - Waris: Invoices for the waris themselves, or for any of their registered children/dependents
 *   (via isChildOfParent, parentId, kinPhone, or direct matching).
 * - Invoices for other members, other families, or other gelanggang are strictly FALSE.
 */
export function isInvoiceRelatedToUser(
  inv: InvoiceItem,
  user?: UserProfile | null,
  members: UserProfile[] = []
): boolean {
  if (!user || !inv) return false;

  // 1. Vendor / 3rd party invoices are strictly excluded for Ahli & Waris
  if (inv.payerType === 'Vendor / Pihak Ketiga' || (inv.memberId && inv.memberId.startsWith('VENDOR-'))) {
    return false;
  }

  const curId = (user.id || '').trim().toLowerCase();
  const curIc = (user.icNo || '').replace(/\D/g, '');
  const curName = (user.name || '').trim().toLowerCase();
  const curPhone = (user.phone || '').replace(/\D/g, '');

  const invMemberId = (inv.memberId || '').trim().toLowerCase();
  const invMemberIc = (inv.memberIcNo || '').replace(/\D/g, '');
  const invMemberName = (inv.memberName || '').trim().toLowerCase();
  const invMemberPhone = (inv.memberPhone || '').replace(/\D/g, '');

  // A Waris who is not a pesilat is NEVER billed personal Yuran Bulanan
  const isPureWaris = (user.role === 'Waris' || (user.roles && user.roles.includes('Waris'))) && !(user as any).isAlsoPesilat;
  if (!isPureWaris || inv.category !== 'Yuran Bulanan') {
    // 2. Direct match with current user (for practicing members)
    if (curId && invMemberId && curId === invMemberId) return true;
    if (curIc && invMemberIc && curIc === invMemberIc) return true;
    if (curName && invMemberName && curName === invMemberName) return true;
    if (curPhone && invMemberPhone && curPhone === invMemberPhone && curName && (invMemberName.includes(curName) || curName.includes(invMemberName))) {
      return true;
    }
  }

  // 3. Child / Dependent match (for Waris and any parent member)
  const myChildren = members.filter(m => {
    if (isChildOfParent(m, user)) return true;
    const mParentId = (m.parentId || '').trim().toLowerCase();
    const mParentIc = (m.parentId || '').replace(/\D/g, '');
    if (curId && mParentId && mParentId === curId) return true;
    if (curIc && mParentIc && mParentIc === curIc) return true;
    if (curPhone && m.kinPhone && m.kinPhone.replace(/\D/g, '') === curPhone) return true;
    return false;
  });

  for (const child of myChildren) {
    const childId = (child.id || '').trim().toLowerCase();
    const childIc = (child.icNo || '').replace(/\D/g, '');
    const childName = (child.name || '').trim().toLowerCase();

    if (childId && invMemberId && childId === invMemberId) return true;
    if (childIc && invMemberIc && childIc === invMemberIc) return true;
    if (childName && invMemberName && (childName === invMemberName || invMemberName.includes(childName) || childName.includes(invMemberName))) return true;

    // Check if invoice title / description mentions child name and matches parent's phone or child's ID
    const titleLower = (inv.title || '').toLowerCase();
    const descLower = (inv.description || '').toLowerCase();
    if (childName && (titleLower.includes(childName) || descLower.includes(childName))) {
      if ((curPhone && invMemberPhone && curPhone === invMemberPhone) || (childId && invMemberId === childId)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Checks whether a user (Ahli or Waris) is a newly registered member whose onboarding/registration
 * payment has NOT yet been confirmed as 'Lulus / Dibayar' by an administrator.
 * 
 * When this is true:
 * - User is restricted to ONLY accessing 'dashboard' and 'payments' (/payments).
 * - Full dashboard features (silibus, keluarga, pengumuman, inventori, dsb.) are locked until an admin
 *   confirms the payment.
 */
export function isPaymentPendingAdminConfirmation(
  user?: UserProfile | null,
  invoices: InvoiceItem[] = [],
  members: UserProfile[] = []
): boolean {
  if (!user) return false;

  // 1. Superadmin and executive management committee are exempt
  if (isSuperAdmin(user)) return false;
  const executiveRoles = ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Pen. Setiausaha', 'Penolong Setiausaha', 'Bendahari', 'AJK', 'Ketua Gelanggang', 'Pemeriksa Kira-Kira', 'Jurulatih'];
  const userRoles = getUserRoles(user);
  if (userRoles.some(r => executiveRoles.includes(r))) {
    return false;
  }

  // 2. Old members / seed members are exempt
  if (user.isOldMember === true) return false;
  if (user.id && /^ASMM-0[0-1][0-9]$/i.test(user.id)) return false;

  // 3. Rejected or on leave
  if (user.status === 'Ditolak' || user.status === 'Cuti') return false;

  // 4. Find all onboarding / registration invoices related to this user or their dependents
  const relevantInvoices = invoices.filter(inv => {
    const isRelated = isInvoiceRelatedToUser(inv, user, members);
    if (!isRelated) return false;

    const isRegCategory =
      inv.category === 'Yuran Pendaftaran' ||
      (inv.title && inv.title.toLowerCase().includes('pendaftaran')) ||
      (inv.description && inv.description.toLowerCase().includes('onboarding')) ||
      inv.id.toLowerCase().includes('onb') ||
      inv.id === user.onboardingInvoiceId;

    return isRegCategory;
  });

  // If there are onboarding invoices in the system for this user/family:
  if (relevantInvoices.length > 0) {
    // If ANY invoice is not yet 'Lulus / Dibayar', payment is pending admin confirmation!
    const hasUnconfirmed = relevantInvoices.some(inv => inv.status !== 'Lulus / Dibayar');
    return hasUnconfirmed;
  }

  // If no onboarding invoice found in database yet:
  // For Ahli: check onboardingStatus
  if (user.role === 'Ahli' && (user.onboardingStatus === 'pending_payment' || !user.onboardingStatus)) {
    return true;
  }

  // For Waris: check if any dependent has pending_payment
  if (user.role === 'Waris') {
    const dependents = members.filter(m => isChildOfParent(m, user));
    if (dependents.length === 0) {
      return false;
    }
    const hasPendingDependent = dependents.some(d => d.onboardingStatus === 'pending_payment');
    if (hasPendingDependent) return true;
  }

  return false;
}

export function getOnboardingPaymentStatus(
  user?: UserProfile | null,
  invoices: InvoiceItem[] = [],
  members: UserProfile[] = []
): {
  isPending: boolean;
  hasUploadedReceipt: boolean;
  unpaidCount: number;
  pendingCount: number;
  paidCount: number;
  totalAmount: number;
} {
  if (!user) {
    return { isPending: false, hasUploadedReceipt: false, unpaidCount: 0, pendingCount: 0, paidCount: 0, totalAmount: 0 };
  }

  const isPending = isPaymentPendingAdminConfirmation(user, invoices, members);
  if (!isPending) {
    return { isPending: false, hasUploadedReceipt: false, unpaidCount: 0, pendingCount: 0, paidCount: 0, totalAmount: 0 };
  }

  const relevantInvoices = invoices.filter(inv => {
    const isRelated = isInvoiceRelatedToUser(inv, user, members);
    if (!isRelated) return false;
    return (
      inv.category === 'Yuran Pendaftaran' ||
      (inv.title && inv.title.toLowerCase().includes('pendaftaran')) ||
      (inv.description && inv.description.toLowerCase().includes('onboarding')) ||
      inv.id.toLowerCase().includes('onb') ||
      inv.id === user.onboardingInvoiceId
    );
  });

  const unpaidCount = relevantInvoices.filter(i => i.status === 'Belum Dibayar' || !i.status).length;
  const pendingCount = relevantInvoices.filter(i => i.status === 'Menunggu Pengesahan').length;
  const paidCount = relevantInvoices.filter(i => i.status === 'Lulus / Dibayar').length;
  const totalAmount = relevantInvoices.reduce((sum, i) => sum + (Number(i.amount) || 0), 0);

  const hasUploadedReceipt = pendingCount > 0 || relevantInvoices.some(i => !!i.receiptUrl || !!i.toyibBillCode);

  return {
    isPending: true,
    hasUploadedReceipt,
    unpaidCount,
    pendingCount,
    paidCount,
    totalAmount
  };
}

/**
 * Standardizes Malaysian bank receipt date & time strings into strict ISO format
 * for database storage and provides user-friendly datepicker bindings and display text.
 */
export function parseReceiptTimestamp(raw?: string | null): { date: string; time: string; full: string; display: string } {
  if (!raw || !raw.trim()) {
    const now = new Date();
    const dStr = now.toISOString().split('T')[0];
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const tStr = `${hours}:${minutes}:${seconds}`;
    return { date: dStr, time: tStr, full: `${dStr} ${tStr}`, display: formatDisplayDate(dStr, tStr) };
  }

  const str = raw.trim();
  const MONTH_MAP: Record<string, string> = {
    jan: '01', januari: '01', january: '01',
    feb: '02', februari: '02', february: '02',
    mac: '03', mar: '03', march: '03',
    apr: '04', april: '04',
    mei: '05', may: '05',
    jun: '06', june: '06',
    jul: '07', july: '07',
    ogo: '08', ogos: '08', aug: '08', august: '08',
    sep: '09', sept: '09', september: '09',
    okt: '10', oct: '10', oktober: '10', october: '10',
    nov: '11', november: '11',
    dis: '12', dec: '12', disember: '12', december: '12',
  };

  let year = '';
  let month = '';
  let day = '';

  // 1. ISO: YYYY-MM-DD
  const isoMatch = str.match(/\b(20\d\d)[-\/.](\d{1,2})[-\/.](\d{1,2})\b/);
  if (isoMatch) {
    year = isoMatch[1];
    month = isoMatch[2].padStart(2, '0');
    day = isoMatch[3].padStart(2, '0');
  }

  // 2. Text month: 5 Sep 2026 or 08 September 2026
  if (!year) {
    const textMonthMatch = str.match(/\b(\d{1,2})\s+([A-Za-z]+)\s+(20\d\d)\b/);
    if (textMonthMatch) {
      day = textMonthMatch[1].padStart(2, '0');
      const mKey = textMonthMatch[2].toLowerCase();
      month = MONTH_MAP[mKey] || '01';
      year = textMonthMatch[3];
    }
  }

  // 3. Text month inverted: Sep 5, 2026 or September 08 2026
  if (!year) {
    const textMonthInverted = str.match(/\b([A-Za-z]+)\s+(\d{1,2}),?\s+(20\d\d)\b/);
    if (textMonthInverted) {
      const mKey = textMonthInverted[1].toLowerCase();
      month = MONTH_MAP[mKey] || '01';
      day = textMonthInverted[2].padStart(2, '0');
      year = textMonthInverted[3];
    }
  }

  // 4. DD/MM/YYYY or DD-MM-YYYY
  if (!year) {
    const dmyMatch = str.match(/\b(\d{1,2})[-\/.](\d{1,2})[-\/.](20\d\d)\b/);
    if (dmyMatch) {
      day = dmyMatch[1].padStart(2, '0');
      month = dmyMatch[2].padStart(2, '0');
      year = dmyMatch[3];
    }
  }

  // Fallback date if none matched
  if (!year || !month || !day) {
    const now = new Date();
    year = String(now.getFullYear());
    month = String(now.getMonth() + 1).padStart(2, '0');
    day = String(now.getDate()).padStart(2, '0');
  }

  const dateStr = `${year}-${month}-${day}`;

  // Time parsing
  let hours = '10';
  let minutes = '00';
  let seconds = '00';

  const timeMatch = str.match(/\b(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])?\b/);
  if (timeMatch) {
    let h = parseInt(timeMatch[1], 10);
    const m = parseInt(timeMatch[2], 10);
    const s = timeMatch[3] ? parseInt(timeMatch[3], 10) : 0;
    const ampm = timeMatch[4]?.toUpperCase();

    if (ampm === 'PM' && h < 12) h += 12;
    if (ampm === 'AM' && h === 12) h = 0;

    hours = String(h).padStart(2, '0');
    minutes = String(m).padStart(2, '0');
    seconds = String(s).padStart(2, '0');
  } else {
    const now = new Date();
    hours = String(now.getHours()).padStart(2, '0');
    minutes = String(now.getMinutes()).padStart(2, '0');
    seconds = String(now.getSeconds()).padStart(2, '0');
  }

  const timeStr = `${hours}:${minutes}:${seconds}`;
  const fullStr = `${dateStr} ${timeStr}`;

  return {
    date: dateStr,
    time: timeStr,
    full: fullStr,
    display: formatDisplayDate(dateStr, timeStr)
  };
}

/**
 * Formats standard date & time into localized readable format (e.g. 19 Okt 2026, 11:05 AM).
 */
export function formatDisplayDate(dateStr?: string, timeStr?: string): string {
  if (!dateStr) return '-';
  try {
    const [y, m, d] = dateStr.split('-');
    const months = ['Jan', 'Feb', 'Mac', 'Apr', 'Mei', 'Jun', 'Jul', 'Ogo', 'Sep', 'Okt', 'Nov', 'Dis'];
    const monthName = months[parseInt(m, 10) - 1] || m;
    
    if (!timeStr) return `${parseInt(d, 10)} ${monthName} ${y}`;
    
    const [hh, mm] = timeStr.split(':');
    let h = parseInt(hh, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${parseInt(d, 10)} ${monthName} ${y}, ${h}:${mm} ${ampm}`;
  } catch (e) {
    return `${dateStr} ${timeStr || ''}`.trim();
  }
}

/**
 * Formats event start date and end date into human-friendly Malay format.
 * Examples:
 * - Single day: "11 Dis 2026"
 * - 2-day event in same month: "11 & 12 Dis 2026"
 * - Date range across months: "30 Nov - 2 Dis 2026"
 */
export function formatEventDates(startDate?: string, endDate?: string): string {
  if (!startDate) return '';
  if (!endDate || endDate === startDate) {
    try {
      const d = new Date(startDate);
      if (isNaN(d.getTime())) return startDate;
      return d.toLocaleDateString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return startDate;
    }
  }

  try {
    const d1 = new Date(startDate);
    const d2 = new Date(endDate);
    if (isNaN(d1.getTime()) || isNaN(d2.getTime())) {
      return `${startDate} - ${endDate}`;
    }

    const sameMonth = d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear();
    const sameYear = d1.getFullYear() === d2.getFullYear();

    const monthShort1 = d1.toLocaleDateString('ms-MY', { month: 'short' });
    const monthShort2 = d2.toLocaleDateString('ms-MY', { month: 'short' });
    const year1 = d1.getFullYear();
    const year2 = d2.getFullYear();

    if (sameMonth) {
      return `${d1.getDate()} & ${d2.getDate()} ${monthShort2} ${year2}`;
    } else if (sameYear) {
      return `${d1.getDate()} ${monthShort1} - ${d2.getDate()} ${monthShort2} ${year2}`;
    } else {
      return `${d1.getDate()} ${monthShort1} ${year1} - ${d2.getDate()} ${monthShort2} ${year2}`;
    }
  } catch {
    return `${startDate} - ${endDate}`;
  }
}

/**
 * Formats event start date and end date into full Malay format.
 * Examples:
 * - Single day: "11 Disember 2026"
 * - 2-day event in same month: "11 & 12 Disember 2026"
 */
export function formatEventDatesFull(startDate?: string, endDate?: string): string {
  if (!startDate) return '';
  if (!endDate || endDate === startDate) {
    try {
      const d = new Date(startDate);
      if (isNaN(d.getTime())) return startDate;
      return d.toLocaleDateString('ms-MY', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return startDate;
    }
  }

  try {
    const d1 = new Date(startDate);
    const d2 = new Date(endDate);
    if (isNaN(d1.getTime()) || isNaN(d2.getTime())) {
      return `${startDate} - ${endDate}`;
    }

    const sameMonth = d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear();
    const monthLong2 = d2.toLocaleDateString('ms-MY', { month: 'long' });
    const year2 = d2.getFullYear();

    if (sameMonth) {
      return `${d1.getDate()} & ${d2.getDate()} ${monthLong2} ${year2}`;
    } else {
      const monthLong1 = d1.toLocaleDateString('ms-MY', { month: 'long' });
      return `${d1.getDate()} ${monthLong1} ${d1.getFullYear()} - ${d2.getDate()} ${monthLong2} ${year2}`;
    }
  } catch {
    return `${startDate} - ${endDate}`;
  }
}

